import React, { Component } from 'react';
import PropTypes from 'prop-types';
import fetch from 'isomorphic-fetch';

const Modal = require('../../../deploynaut/js/Modal.jsx');

const withSudoMode = (WrappedComponent) => {
	class ComponentWithSudoMode extends Component {
		constructor(props) {
			super(props);

			this.state = {
				active: this.props.sudoModeActive || false,
				loading: false,
				errorMessage: null,
			};

			this.handleVerify = this.handleVerify.bind(this);
			this.handleVerifyInputKeyPress = this.handleVerifyInputKeyPress.bind(this);

			// React 15 compatible ref callback
			this.passwordInput = null;
			this.setPasswordInput = (element) => {
				this.passwordInput = element;
			};
		}

		/**
		 * Action called when the user has entered their password and requested
		 * verification of sudo mode state.
		 */
		handleVerify() {
			this.setState({
				loading: true,
			});

			const payload = new FormData();
			payload.append('SecurityID', this.props.SecurityID);
			payload.append('Password', this.passwordInput.value);

			// Validate the request
			fetch(this.props.endpoints.activate, {
				method: 'POST',
				body: payload,
			}).then(response => response.json().then((result) => {
				// Happy path, send the user to the wrapped component
				if (result.result) {
					return this.setState({
						loading: false,
						active: true,
					});
				}

				// Validation error, show them the message
				return this.setState({
					loading: false,
					errorMessage: result.message,
				}, () => this.passwordInput.focus());
			}));
		}

		/**
		 * Treat pressing enter on the password field the same as clicking the
		 * verify button.
		 *
		 * @param {object} event
		 */
		handleVerifyInputKeyPress(event) {
			this.setState({
				errorMessage: null
			});

			if (event.charCode === 13) {
				event.stopPropagation();
				event.preventDefault();
				this.handleVerify();
			}
		}

		/**
		 * Returns whether "sudo mode" is active for the current user.
		 *
		 * @returns {boolean}
		 */
		isSudoModeActive() {
			return this.state.active === true;
		}

		/**
		 * Renders the password verification form to enter sudo mode
		 *
		 * @returns {HTMLElement}
		 */
		renderSudoModeVerification() {
			const { ss: { i18n } } = window;
			const { errorMessage } = this.state;

			const inputProps = {
				type: 'password',
				name: 'sudoModePassword',
				id: 'sudoModePassword',
				className: 'form-control no-change-track',
				onKeyPress: this.handleVerifyInputKeyPress,
			};

			let formClasses = 'form-group';
			if (errorMessage) {
				formClasses += ' has-error'
			}

			return (
				<div className="sudo-mode__verify">
					<form className="sudo-mode__verify-form-group">
						<label htmlFor="sudoModePassword">
							{ i18n._t('SudoMode.ENTER_PASSWORD', 'Enter your password') }
						</label>

						<div className={formClasses}>
							<input ref={this.setPasswordInput} {...inputProps} />
							<span className="help-block">{ errorMessage }</span>
						</div>

						<button
							type="button"
							className="btn btn-primary sudo-mode__verify-button"
							onClick={this.handleVerify}
						>
							{ i18n._t('SudoMode.VERIFY', 'Verify') }
						</button>
					</form>
				</div>
			);
		}

		handleCancellation() {
			if (!this.isSudoModeActive()) {
				this.props.cancelSudoModeHandler();
			}
		}

		/**
		 * Renders the "sudo mode" notice or verification screen
		 *
		 * @returns {HTMLElement}
		 */
		renderSudoMode() {
			const { ss: { i18n } } = window;

			return (
				<Modal
					closeHandler={() => this.handleCancellation()}
					className="sudo-mode-modal"
					large={false}
					title={ i18n._t('SudoMode.VERIFY_ITS_YOU', 'Verify it\'s you') }
				>
					<div className="sudo-mode__container">
							{ this.renderSudoModeVerification() }
					</div>
				</Modal>
			);
		}

		render() {
			if (!this.isSudoModeActive()) {
				return this.renderSudoMode();
			}

			return <WrappedComponent {...this.props} />;
		}
	}

	ComponentWithSudoMode.propTypes = {
		LoadingComponent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
		cancelSudoModeHandler: PropTypes.func
	};

	return ComponentWithSudoMode;
};

export default withSudoMode;
